import { InvoiceFullProductData } from '@/interfaces/billing'

/**
 * Gets the main price for a product based on price system priority
 * @param product - The product data containing prices array
 * @param priceSystemId - The current price system ID
 * @returns The price amount as a number
 */
export const getProductMainPrice = (
  product: InvoiceFullProductData,
  priceSystemId?: number
): number => {
  if (!product.prices || product.prices.length === 0) {
    return 0
  }

  // Priority 1: Find price for current price system
  if (priceSystemId) {
    const systemPrice = product.prices.find(
      (price) => price.priceSystemId === priceSystemId
    )
    if (systemPrice) {
      return systemPrice.price
    }
  }

  // Priority 2: Find main price (isMain: true)
  const mainPrice = product.prices.find((price) => price.isMain)
  if (mainPrice) {
    return mainPrice.price
  }

  // Priority 3: Use first available price as fallback
  return product.prices[0]?.price || 0
}

/**
 * Gets the main currency for a product based on price system priority
 * @param product - The product data containing prices array
 * @param priceSystemId - The current price system ID
 * @returns The currency code as a string
 */
export const getProductMainCurrency = (
  product: InvoiceFullProductData,
  priceSystemId?: number
): string => {
  if (!product.prices || product.prices.length === 0) {
    return 'USD'
  }

  // Priority 1: Find currency for current price system
  if (priceSystemId) {
    const systemPrice = product.prices.find(
      (price) => price.priceSystemId === priceSystemId
    )
    if (systemPrice) {
      return systemPrice.codeCurrency
    }
  }

  // Priority 2: Find main price currency (isMain: true)
  const mainPrice = product.prices.find((price) => price.isMain)
  if (mainPrice) {
    return mainPrice.codeCurrency
  }

  // Priority 3: Use first available currency as fallback
  return product.prices[0]?.codeCurrency || 'USD'
}

/**
 * Gets both price and currency for a product in a single call
 * @param product - The product data containing prices array
 * @param priceSystemId - The current price system ID
 * @returns Object with price and currency
 */
export const getProductPriceAndCurrency = (
  product: InvoiceFullProductData,
  priceSystemId?: number
): { price: number; currency: string } => {
  return {
    price: getProductMainPrice(product, priceSystemId),
    currency: getProductMainCurrency(product, priceSystemId),
  }
}
